import React, { useEffect, useRef, useState } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { paymentCardGradient } from '@/components/payment/PaymentCardGradientStyles';
import {
  NMI_FIELD_SHELL_CLASS,
  nmiCollectParentStyles,
  nmiCollectStyleConfig,
} from '@/components/defera-capital-nmi/nmiCollectFieldStyles';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { deferaCapitalNmiUrl } from '@/lib/apiUrls';
import { mergeSupabaseEdgeHeaders } from '@/lib/reserveActAs';
import { nmiDeclineMessage } from '@/lib/nmiDeclineMessage';
import { extractNmiCardDisplayFromCollectJs } from '@/utils/nmiCardDisplay';
import NmiCardOnFileDisplay from '@/components/defera-capital-nmi/NmiCardOnFileDisplay';
import { cn } from '@/lib/utils';
import { MODAL_GHOST_BTN_CLASS, MODAL_PRIMARY_BTN_CLASS } from '@/components/booking/paymentScheduleModalStyles';

/**
 * Defera Capital — NMI Update Card form.
 *
 * Parallel of `DeferaZeroUpdateCardForm` (Stripe). Mounted by
 * `PaymentScheduleDisplay` when the booking is on the NMI processor track
 * (`flight_data._payment_processor === 'nmi'`).
 *
 * UX difference from Stripe:
 *   - NMI has a single vault per customer. There is no "pick a different
 *     saved card" picker — replacing the card always overwrites the vault.
 *     This matches NMI's data model and avoids confusion: customers see one
 *     card on file, they replace it.
 *   - Card collection happens through Collect.js inline (hosted iframe
 *     fields). Card data never touches our DOM.
 *   - On submit we POST the one-time `payment_token` to
 *     `defera-capital-nmi-update-customer-vault`, which keeps the same
 *     vault id and rewrites the underlying card. All existing
 *     `nmi_installment_attempts` keep working — the next daily cron tick
 *     charges the new card.
 *
 * Future installments fire automatically against the new vault — there is
 * no separate "set as default" toggle because NMI's vault is single-card
 * by design.
 */

const COLLECT_JS_BASE = (typeof window !== 'undefined' && window.NMI_API_BASE_URL)
  ? window.NMI_API_BASE_URL
  : 'https://vectorpayments.transactiongateway.com';

const COLLECT_JS_SCRIPT_ID = 'nmi-collect-js';

/**
 * Singleton loader with data-attribute swap.
 *
 * If a previous component instance loaded Collect.js with a different
 * signature (e.g. different tokenization key from another tab), we swap
 * the tag so the new signature wins.
 */
function loadCollectJs(tokenizationKey, dataAttrs = {}) {
  if (typeof window === 'undefined') return Promise.reject(new Error('SSR'));
  const existing = document.getElementById(COLLECT_JS_SCRIPT_ID);
  const desiredSig = JSON.stringify({ tokenizationKey, ...dataAttrs });
  // Use a plain JS expando property (`_deferaSig`), NOT a `data-*`
  // attribute. Collect.js strictly allowlists data-* attrs on its own
  // script tag and throws `"Unexpected fields for <name>"` when it sees
  // an unknown one — which silently aborts init and leaves
  // `window.CollectJS` undefined. See checkout form for full details.
  if (existing && existing._deferaSig !== desiredSig) {
    try { delete window.CollectJS; } catch { /* property may be non-configurable */ }
    existing.remove();
  }
  if (window.CollectJS && existing && existing._deferaSig === desiredSig) {
    return Promise.resolve(window.CollectJS);
  }
  const current = document.getElementById(COLLECT_JS_SCRIPT_ID);
  if (current) {
    return new Promise((resolve, reject) => {
      current.addEventListener('load', () => resolve(window.CollectJS), { once: true });
      current.addEventListener('error', () => reject(new Error('Collect.js failed to load')), { once: true });
    });
  }
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.id = COLLECT_JS_SCRIPT_ID;
    s.src = `${COLLECT_JS_BASE}/token/Collect.js`;
    if (tokenizationKey) s.setAttribute('data-tokenization-key', tokenizationKey);
    for (const [k, v] of Object.entries(dataAttrs)) {
      if (v == null || v === false) continue;
      s.setAttribute(k, String(v));
    }
    s._deferaSig = desiredSig;
    s.async = true;
    s.onload = () => resolve(window.CollectJS);
    s.onerror = () => reject(new Error('Collect.js failed to load'));
    document.head.appendChild(s);
  });
}

/**
 * This form is card-only. There is no Apple Pay path on Defera Capital
 * (NMI). If we add Apple Pay later, the right move is to use Collect.js's
 * inline wallet support — NMI's hosted Collect Checkout was tried and
 * abandoned because Apple Pay didn't surface on the merchant's hosted page.
 */

export default function DeferaCapitalNmiUpdateCardForm({ tripId, flightData = null, onClose, onSuccess }) {
  const [sessionInfo, setSessionInfo] = useState(null); // {tokenization_key, customer_vault_id}
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [collectReady, setCollectReady] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const onTokenRef = useRef(null);
  const submitLockRef = useRef(false);

  // Step 1: ask the server for tokenization key + the vault id we'll replace.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) throw new Error('Please sign in to update your card');
        const res = await fetch(deferaCapitalNmiUrl('create-update-card-session'), {
          method: 'POST',
          headers: mergeSupabaseEdgeHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }),
          body: JSON.stringify({ booking_id: tripId }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || 'Could not start card update');
        if (!cancelled) setSessionInfo(data);
      } catch (e) {
        if (!cancelled) setLoadError(e.message || 'Could not start card update');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tripId]);

  // Step 2: once we have a tokenization key, configure Collect.js inline fields.
  useEffect(() => {
    if (!sessionInfo?.tokenization_key) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const CollectJS = await loadCollectJs(
          sessionInfo.tokenization_key,
          {}, // Card-only.
        );
        if (cancelled) return;
        CollectJS.configure({
          variant: 'inline',
          styleSniffer: false,
          tokenizationKey: sessionInfo.tokenization_key,
          fields: {
            ccnumber: { selector: '#dc-nmi-update-ccnumber', title: 'Card Number', placeholder: 'Card number' },
            ccexp: { selector: '#dc-nmi-update-ccexp', title: 'Expiration', placeholder: 'MM / YY' },
            cvv: { display: 'show', selector: '#dc-nmi-update-cvv', title: 'CVV', placeholder: 'CVC' },
          },
          // See checkout form for the centering strategy — line-height
          // matches the wrapper height so the browser's baseline math
          // centers the glyphs/caret in the vertical middle of the
          // 44px line-box, regardless of what margin Collect.js's
          // iframe body uses.
          ...nmiCollectStyleConfig(),
          fieldsAvailableCallback: () => { if (!cancelled) setCollectReady(true); },
          callback: (resp) => {
            const inflight = onTokenRef.current;
            if (inflight) {
              inflight(resp);
            }
          },
        });
      } catch (e) {
        if (!cancelled) setLoadError('Could not load card collection. Please refresh and try again.');
      }
    })();
    return () => { cancelled = true; };
  }, [sessionInfo?.tokenization_key]);

  /**
   * Token → vault-update path. Called after Collect.js's tokenization
   * callback hands back a one-time payment token.
   */
  const submitVaultUpdate = async (paymentToken, cardDisplay) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('Please sign in to continue');

    const updateRes = await fetch(deferaCapitalNmiUrl('update-customer-vault'), {
      method: 'POST',
      headers: mergeSupabaseEdgeHeaders({ 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` }),
      body: JSON.stringify({
        booking_id: tripId,
        payment_token: paymentToken,
        ...(cardDisplay ? { card_display: cardDisplay } : {}),
      }),
    });
    const updateData = await updateRes.json().catch(() => ({}));
    if (!updateRes.ok) {
      const friendly = nmiDeclineMessage(updateData, {
        defaultMessage: "We couldn't update your card. Please try again or use a different card.",
      });
      console.error('[defera-capital-nmi] update-card error', {
        rawText: friendly.dev,
        error_type: updateData?.error_type,
        gateway_response: updateData?.gateway_response,
        gateway_response_code: updateData?.gateway_response_code,
      });
      const err = new Error(friendly.customer);
      err.nmiAction = friendly.action;
      throw err;
    }

    toast.success('Card updated. Future installments will use your new card.');
    onSuccess?.();
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isProcessing || submitLockRef.current) return;
    submitLockRef.current = true;
    setIsProcessing(true);
    setError(null);
    try {
      if (!window.CollectJS || !collectReady) throw new Error('Card form not ready yet');
      // Wait for Collect.js to hand back a one-time token.
      const tokenResp = await new Promise((resolve, reject) => {
        onTokenRef.current = (resp) => {
          onTokenRef.current = null;
          if (resp?.token) resolve(resp);
          else reject(new Error(resp?.error || 'Tokenization failed'));
        };
        try { window.CollectJS.startPaymentRequest(); }
        catch (ex) { onTokenRef.current = null; reject(ex); }
      });

      await submitVaultUpdate(
        tokenResp.token,
        extractNmiCardDisplayFromCollectJs(tokenResp),
      );
    } catch (err) {
      const msg = err?.message || 'Could not update card';
      setError(msg);
      toast.error(msg, { description: err?.nmiAction || undefined });
    } finally {
      setIsProcessing(false);
      submitLockRef.current = false;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-white/40" />
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl">
        <p className="text-red-400 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          {loadError}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className={paymentCardGradient.container}>
        <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-normal mb-3">
          Card on file now
        </p>
        <NmiCardOnFileDisplay
          flightData={flightData}
          fallbackText="We have a card saved for this trip, but we can't show its details. Enter your new card below to replace it."
        />
        <p className="text-white/60 text-xs font-normal mt-3 leading-relaxed">
          Future installments charge this card until you save a new one below.
        </p>
      </div>

      <style>{nmiCollectParentStyles('#dc-nmi-update-ccnumber, #dc-nmi-update-ccexp, #dc-nmi-update-cvv')}</style>
      <div className={paymentCardGradient.container}>
        <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-normal mb-4">
          New card for installments
        </p>
        {/* Card-only — see the top of this file for the Apple Pay rationale. */}
        <div className="space-y-3">
          <div>
            <label className="text-[11px] text-white/40 uppercase tracking-wider block mb-1.5">
              Card Number
            </label>
            <div className={NMI_FIELD_SHELL_CLASS}>
              <div id="dc-nmi-update-ccnumber" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-white/40 uppercase tracking-wider block mb-1.5">
                Expiration
              </label>
              <div className={NMI_FIELD_SHELL_CLASS}>
                <div id="dc-nmi-update-ccexp" />
              </div>
            </div>
            <div>
              <label className="text-[11px] text-white/40 uppercase tracking-wider block mb-1.5">
                CVV
              </label>
              <div className={NMI_FIELD_SHELL_CLASS}>
                <div id="dc-nmi-update-cvv" />
              </div>
            </div>
          </div>
        </div>
      </div>
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl">
          <p className="text-red-400 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {error}
          </p>
        </div>
      )}
      <div className="flex gap-2.5">
        <Button type="button" variant="ghost" onClick={onClose} className={cn(MODAL_GHOST_BTN_CLASS, 'flex-1')}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isProcessing || !collectReady}
          className={cn(MODAL_PRIMARY_BTN_CLASS, 'flex-1')}
        >
          {isProcessing ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>) : 'Update Card'}
        </Button>
      </div>
    </form>
  );
}
